export type BatteryProposalThemeName = `dark` | `powur-light`

export type BatteryProposalThemeTokens = {
  page: string
  base: string
  surface: string
  elevated: string
  elevated2: string
  glass: string
  glassHi: string
  border: string
  borderStrong: string
  borderStrongAlpha: number
  text: string
  textSoft: string
  textMuted: string
  textMutedAlpha: number
  textDim: string
  textDimAlpha: number
  accent: string
  accentHover: string
  accentSoft: string
  onAccent: string
  cyan: string
  gold: string
  success: string
  successSoft: string
  danger: string
  gradAccent: string
}

const channelToLinear = (channel: number): number => {
  const value = channel / 255
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
}

const relativeLuminance = (hex: string): number => {
  const raw = hex.replace(`#`, ``)
  const red = channelToLinear(parseInt(raw.slice(0, 2), 16))
  const green = channelToLinear(parseInt(raw.slice(2, 4), 16))
  const blue = channelToLinear(parseInt(raw.slice(4, 6), 16))
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue
}

export const contrastRatio = (
  foreground: string,
  background: string,
): number => {
  const lighter = Math.max(
    relativeLuminance(foreground),
    relativeLuminance(background),
  )
  const darker = Math.min(
    relativeLuminance(foreground),
    relativeLuminance(background),
  )
  return (lighter + 0.05) / (darker + 0.05)
}

export const BATTERY_PROPOSAL_THEMES: Record<
  BatteryProposalThemeName,
  BatteryProposalThemeTokens
> = {
  dark: {
    page: `#05070d`,
    base: `#0a0e18`,
    surface: `#11162a`,
    elevated: `#161c34`,
    elevated2: `#1d2542`,
    glass: `rgba(255, 255, 255, 0.04)`,
    glassHi: `rgba(255, 255, 255, 0.07)`,
    border: `rgba(255, 255, 255, 0.12)`,
    borderStrong: `rgba(255, 255, 255, 0.36)`,
    borderStrongAlpha: 0.36,
    text: `#ffffff`,
    textSoft: `rgba(255, 255, 255, 0.88)`,
    textMuted: `rgba(255, 255, 255, 0.72)`,
    textMutedAlpha: 0.72,
    textDim: `rgba(255, 255, 255, 0.6)`,
    textDimAlpha: 0.6,
    accent: `#4a9cff`,
    accentHover: `#6ab0ff`,
    accentSoft: `rgba(74, 156, 255, 0.14)`,
    onAccent: `#05070d`,
    cyan: `#2dd4ee`,
    gold: `#f4c751`,
    success: `#34d399`,
    successSoft: `rgba(52, 211, 153, 0.14)`,
    danger: `#f87171`,
    gradAccent: `linear-gradient(135deg, #4a9cff 0%, #2dd4ee 100%)`,
  },
  [`powur-light`]: {
    page: `#f1f5f8`,
    base: `#ffffff`,
    surface: `#ffffff`,
    elevated: `#f8fbfd`,
    elevated2: `#e7f3fa`,
    glass: `rgba(0, 33, 96, 0.025)`,
    glassHi: `rgba(0, 33, 96, 0.06)`,
    border: `#d6e1f0`,
    borderStrong: `#b1c2d9`,
    borderStrongAlpha: 1,
    text: `#002160`,
    textSoft: `#0d2d52`,
    textMuted: `#5e676c`,
    textMutedAlpha: 1,
    textDim: `#577197`,
    textDimAlpha: 1,
    accent: `#0070f4`,
    accentHover: `#0058c4`,
    accentSoft: `rgba(0, 112, 244, 0.1)`,
    onAccent: `#ffffff`,
    cyan: `#0a7a99`,
    gold: `#f4c751`,
    success: `#019e6f`,
    successSoft: `rgba(1, 158, 111, 0.1)`,
    danger: `#ea1a0b`,
    gradAccent: `linear-gradient(95.7deg, #0a7a99 12.17%, #0070f4 93.24%)`,
  },
}

export const PINNED_BATTERY_PROPOSAL_THEME: BatteryProposalThemeName = `dark`
